import React, {
    useMemo,
    useContext,
    createContext,
    PropsWithChildren,
} from 'react'
import { InternalChatMessageFieldsFragment } from '~graphql/generated/graphql'

export default function TeamChatMessagePropertiesProvider({
    message,
    messageIndex,
    allMessages,
    children,
}: PropsWithChildren<{
    messageIndex: number
    children: React.ReactNode
    message: InternalChatMessageFieldsFragment
    allMessages: InternalChatMessageFieldsFragment[]
}>) {
    const lastMessageSender = useMemo(
        () => allMessages[messageIndex - 1]?.sender,
        [allMessages, messageIndex],
    )
    const lastMessageTime = useMemo(
        () => allMessages[messageIndex - 1]?.created_at,
        [allMessages, messageIndex],
    )

    const LAST_MESSAGE_TIME_THRESHOLD_LG = 1000 * 60 * 60 * 12 // 12 hours
    const LAST_MESSAGE_TIME_THRESHOLD_SM = 1000 * 60 * 5 // 5 minutes

    const messageTimeDifference = useMemo(
        () => message.created_at - lastMessageTime,
        [message, lastMessageTime],
    )

    const isPastLgThreshold = useMemo(
        () => messageTimeDifference > LAST_MESSAGE_TIME_THRESHOLD_LG,
        [messageTimeDifference],
    )
    const isPastSmThreshold = useMemo(
        () =>
            messageTimeDifference > LAST_MESSAGE_TIME_THRESHOLD_SM ||
            lastMessageSender?.user.id !== message.sender.user.id,
        [messageTimeDifference],
    )

    const isLast = useMemo(
        () => messageIndex === allMessages.length - 1,
        [messageIndex, allMessages],
    )
    const isFirst = useMemo(() => messageIndex === 0, [messageIndex])

    return (
        <TeamChatMessagePropertiesContext.Provider
            value={{
                message,
                messageIndex,
                allMessages,

                // Message properties
                isPastLgThreshold,
                isPastSmThreshold,
                isLast,
                isFirst,
            }}
        >
            {children}
        </TeamChatMessagePropertiesContext.Provider>
    )
}

type TeamChatMessagePropertiesContextType = {
    message: InternalChatMessageFieldsFragment
    messageIndex: number
    allMessages: InternalChatMessageFieldsFragment[]

    // Message properties
    isPastLgThreshold: boolean
    isPastSmThreshold: boolean
    isLast: boolean
    isFirst: boolean
}

const TeamChatMessagePropertiesContext =
    createContext<TeamChatMessagePropertiesContextType>(
        {} as TeamChatMessagePropertiesContextType,
    )

export function useTeamChatMessageProperties() {
    return useContext(TeamChatMessagePropertiesContext)
}
